import { computed, ref, shallowRef, toRaw, watch } from "vue";
import { uuid } from "vue-uuid";
import { CMM_ATTACHMENT_BUDGET_BYTES, canChoosePagesFrom, emptyChartDoc, failureMessage, formatMb, MAX_AI_ONLY_DOCUMENTS, isPreparingStatus, prepareChartFromKey, subsetChart, uploadChartFile, } from "@/composables/useChartUpload";
import { parseUploadError, uploadService } from "@/services/uploadService";
import { isOurBucketLink } from "@/utils/s3Links";
import { tryParseApiErrors } from "@/utils";
const firstLinks = (links) => (links ?? []).filter(Boolean);
const linkKey = (link) => link.split("?")[0];
export function useOrderDocumentRows(options) {
    const pending = ref([]);
    const notice = ref(null);
    const views = new WeakMap();
    const charts = () => options.patientChart() ?? [];
    const references = () => options.aiReferenceDocuments() ?? [];
    const setReferences = (next) => {
        const kept = new Set(next.flatMap((entry) => entry.links));
        const removed = references().flatMap((entry) => firstLinks(entry.links).filter((link) => !kept.has(link)));
        options.setReferences(next, removed);
    };
    const viewOf = (key, fill) => {
        const fresh = fill();
        const view = views.get(key);
        if (!view) {
            views.set(key, fresh);
            return fresh;
        }
        return Object.assign(view, fresh);
    };
    const preparedFields = (link) => {
        const facts = link ? options.knownFacts(link) : null;
        return {
            droppedPages: facts?.droppedPages ?? [],
            originBytes: facts?.originBytes ?? null,
            level: facts?.level ?? null,
        };
    };
    const chartRow = (entry) => viewOf(entry, () => {
        const links = firstLinks(entry.links);
        return {
            ...preparedFields(links[0]),
            label: entry.label ?? null,
            links,
            sendToPayer: true,
            preparing: !links.length && isPreparingStatus(entry.status),
            error: !links.length && entry.status === "error" ? entry.error ?? null : null,
            sizeBytes: links.length ? options.knownSize(links[0]) : null,
            requested: !links.length && (!entry.uploadKey || entry.status === "error"),
            labelLocked: true,
            payerLocked: !links.length || entry.requestedByPayer === true || !isOurBucketLink(links[0], options.documentBuckets()),
            saved: true,
        };
    });
    const referenceRow = (entry) => viewOf(entry, () => {
        const links = firstLinks(entry.links);
        return {
            ...preparedFields(links[0]),
            sizeBytes: links.length ? options.knownSize(links[0]) : null,
            label: entry.label,
            links,
            sendToPayer: false,
            saved: true,
        };
    });
    const keyOf = (source) => {
        if (source.kind === "pending")
            return toRaw(source.upload);
        const [link] = firstLinks(source.entry.links);
        return link ? linkKey(link) : toRaw(source.entry);
    };
    const loadOrder = computed(() => [
        ...charts().map((entry) => ({ kind: "chart", entry })),
        ...pending.value.map((upload) => ({ kind: "pending", upload })),
        ...references().map((entry) => ({ kind: "reference", entry })),
    ]);
    const order = shallowRef([]);
    watch(loadOrder, (current) => {
        const keys = current.map(keyOf);
        const present = new Set(keys);
        const kept = order.value.filter((key) => present.has(key));
        const known = new Set(kept);
        order.value = [...kept, ...keys.filter((key) => !known.has(key))];
    }, { immediate: true });
    const rekey = (from, to, also = null) => {
        const [fromKey, alsoKey] = [toRaw(from), also && toRaw(also)];
        order.value = order.value.filter((key) => key !== alsoKey).map((key) => (key === fromKey ? to : key));
    };
    /** Rows keep the place they first took: ticking Send to payer moves an entry between lists, not up or down. */
    const sources = computed(() => {
        const position = new Map(order.value.map((key, index) => [key, index]));
        return [...loadOrder.value].sort((a, b) => (position.get(keyOf(a)) ?? Infinity) - (position.get(keyOf(b)) ?? Infinity));
    });
    const rows = computed(() => sources.value.map((source) => {
        if (source.kind === "chart")
            return chartRow(source.entry);
        if (source.kind === "reference")
            return referenceRow(source.entry);
        return source.upload;
    }));
    const payerLinks = () => charts().flatMap((entry) => firstLinks(entry.links).slice(0, 1));
    const budgetLinks = () => [...payerLinks(), ...options.otherBudgetLinks()];
    const budgetCaption = computed(() => {
        if (!options.cmmBudgeted())
            return null;
        const sizes = [...payerLinks().map(options.knownSize), ...options.otherBudgetLinks().map(options.knownSize)];
        if (!sizes.length || sizes.some((size) => size == null))
            return null;
        const used = sizes.reduce((sum, size) => sum + (size ?? 0), 0);
        const budget = CMM_ATTACHMENT_BUDGET_BYTES;
        if (used > budget) {
            return `${formatMb(used)} MB of ${formatMb(budget)} MB attachment budget used · ${formatMb(used - budget)} MB over. Remove a file or untick Send to payer.`;
        }
        return `${formatMb(used)} MB of ${formatMb(budget)} MB attachment budget used · ${formatMb(budget - used)} MB left`;
    });
    const isUploading = () => pending.value.some((upload) => upload.uploading || upload.preparing);
    const finish = (upload, finished) => {
        const { link, sizeBytes } = finished;
        const target = upload.target && charts().includes(upload.target) ? upload.target : null;
        if (target)
            rekey(target, linkKey(link), upload);
        else
            rekey(upload, linkKey(link));
        options.rememberPrepared(link, {
            sizeBytes: sizeBytes ?? undefined,
            originBytes: upload.file?.size ?? null,
            droppedPages: finished.droppedPages,
            pageRanges: finished.pageRanges,
            level: finished.level ?? null,
        });
        pending.value = pending.value.filter((item) => item !== upload);
        const label = upload.label || upload.file?.name || "Document";
        if (target) {
            if (!target.uploadKey)
                target.requestedByPayer = true;
            options.addDocument({ storeVar: "patientChart", chartEntry: target, link, sizeBytes });
        }
        else if (upload.target || upload.sendToPayer) {
            options.addDocument({ storeVar: "patientChart", newEntry: true, fileName: label, link, sizeBytes });
        }
        else {
            setReferences([...references(), { label, links: [link], context: "" }]);
        }
    };
    const failWith = (upload, message, details = null) => {
        upload.error = message;
        upload.errorDetails = details;
    };
    const process = async (upload, file) => {
        upload.uploading = true;
        try {
            if (!options.cmmBudgeted()) {
                const { downloadUrl } = await uploadService.uploadFileWithKey(file, `${uuid.v4()}/${file.name}`);
                upload.uploading = false;
                if (pending.value.includes(upload))
                    finish(upload, { link: downloadUrl, sizeBytes: file.size });
                return;
            }
            const uploaded = await uploadChartFile(file);
            upload.uploading = false;
            if (!pending.value.includes(upload))
                return;
            upload.sourceUploadKey = uploaded.uploadKey;
            upload.preparing = true;
            await inTurn(() => prepare(upload, uploaded.uploadKey));
        }
        catch (error) {
            failWith(upload, parseUploadError(error));
        }
        finally {
            upload.uploading = false;
        }
    };
    let prepareQueue = Promise.resolve();
    /** Runs prepares one at a time, so each one is measured against the files finished before it. */
    const inTurn = (job) => {
        const run = prepareQueue.then(job, job);
        prepareQueue = run.catch(() => undefined);
        return run;
    };
    /** Prepares an upload against the payer budget, or the whole budget once it is AI-only; an untick mid-prepare retries alone. */
    const prepare = async (upload, key) => {
        const alone = !upload.sendToPayer;
        upload.error = null;
        upload.errorDetails = null;
        upload.preparing = true;
        upload.payerLocked = alone || upload.target !== null;
        try {
            const outcome = await prepareChartFromKey(key, { otherLinks: alone ? [] : budgetLinks() });
            if (!pending.value.includes(upload))
                return;
            if (outcome.status !== "error")
                finish(upload, { ...outcome, link: outcome.downloadUrl });
            else if (!alone && !upload.sendToPayer)
                await prepare(upload, key);
            else
                failWith(upload, failureMessage(outcome, !alone && payerLinks().length > 0), outcome.errorDetails);
        }
        catch (error) {
            failWith(upload, tryParseApiErrors(error)[0]);
        }
        finally {
            upload.preparing = false;
            upload.payerLocked = upload.target !== null;
        }
    };
    const start = (file, target) => {
        const upload = {
            ...emptyChartDoc(),
            label: target?.label ?? file.name,
            file,
            target,
            errorDetails: null,
            payerLocked: target !== null,
        };
        pending.value = [...pending.value, upload];
        void process(pending.value[pending.value.length - 1], file);
    };
    const addFiles = (files) => files.forEach((file) => start(file, null));
    const uploadInto = (index, file) => {
        const source = sources.value[index];
        if (source?.kind === "chart")
            start(file, source.entry);
    };
    const removeChartEntry = (entry) => {
        const links = firstLinks(entry.links);
        if (!links.length) {
            options.deleteDocument({ storeVar: "patientChart", chartEntry: entry, label: entry.label, isDelete: true });
            return;
        }
        links.forEach((link) => options.deleteDocument({ storeVar: "patientChart", chartEntry: entry, label: entry.label, link, isDelete: true }));
    };
    const remove = (index) => {
        notice.value = null;
        const source = sources.value[index];
        if (!source)
            return;
        if (source.kind === "pending")
            pending.value = pending.value.filter((item) => item !== source.upload);
        else if (source.kind === "reference")
            setReferences(references().filter((entry) => entry !== source.entry));
        else
            removeChartEntry(source.entry);
    };
    const setSendToPayer = (index, sendToPayer) => {
        notice.value = null;
        const source = sources.value[index];
        if (!source)
            return;
        if (source.kind === "pending") {
            const { upload } = source;
            if (upload.payerLocked)
                return;
            const otherAiOnly = pending.value.filter((item) => item !== upload && !item.sendToPayer).length;
            if (!sendToPayer && references().length + otherAiOnly >= MAX_AI_ONLY_DOCUMENTS) {
                notice.value = `At most ${MAX_AI_ONLY_DOCUMENTS} files can be for our autocomplete tool only.`;
                return;
            }
            upload.sendToPayer = sendToPayer;
            if (!sendToPayer && upload.error && upload.sourceUploadKey && !upload.uploading && !upload.preparing) {
                const key = upload.sourceUploadKey;
                upload.preparing = true;
                void inTurn(() => prepare(upload, key));
            }
            return;
        }
        if (source.kind === "chart" && !sendToPayer) {
            const links = firstLinks(source.entry.links);
            const pendingAiOnly = pending.value.filter((item) => !item.sendToPayer).length;
            if (references().length + pendingAiOnly + links.length > MAX_AI_ONLY_DOCUMENTS) {
                notice.value = `At most ${MAX_AI_ONLY_DOCUMENTS} files can be for our autocomplete tool only.`;
                return;
            }
            const label = source.entry.label || "Document";
            setReferences([...references(), ...links.map((link) => ({ label, links: [link], context: "" }))]);
            removeChartEntry(source.entry);
            return;
        }
        if (source.kind === "reference" && sendToPayer) {
            const [link] = firstLinks(source.entry.links);
            if (link && options.cmmBudgeted()) {
                const sizes = [link, ...budgetLinks()].map(options.knownSize);
                const total = sizes.reduce((sum, size) => sum + (size ?? 0), 0);
                if (sizes.every((size) => size != null) && total > CMM_ATTACHMENT_BUDGET_BYTES) {
                    notice.value = `This file does not fit the ${formatMb(CMM_ATTACHMENT_BUDGET_BYTES)} MB attachment budget with the other payer files. Untick or remove one of them first.`;
                    return;
                }
            }
            setReferences(references().filter((entry) => entry !== source.entry));
            if (link) {
                options.addDocument({
                    storeVar: "patientChart",
                    newEntry: true,
                    fileName: source.entry.label,
                    link,
                    sizeBytes: options.knownSize(link),
                });
            }
        }
    };
    const updateLabel = (index, label) => {
        const source = sources.value[index];
        if (!source)
            return;
        if (source.kind === "pending")
            source.upload.label = label;
        else if (source.kind === "reference")
            source.entry.label = label;
    };
    const pickerUpload = ref(null);
    const pickerProps = computed(() => {
        const details = pickerUpload.value?.errorDetails;
        if (!details?.bestDownloadUrl || !details.perPageBytes?.length)
            return null;
        return {
            downloadUrl: details.bestDownloadUrl,
            perPageBytes: details.perPageBytes,
            budgetBytes: details.budgetBytes,
            allowOverBudget: false,
        };
    });
    const canChoosePages = (row) => canChoosePagesFrom(pending.value.find((upload) => upload === row)?.errorDetails ?? null);
    const openPicker = (index) => {
        const source = sources.value[index];
        if (source?.kind === "pending")
            pickerUpload.value = source.upload;
    };
    const closePicker = () => {
        pickerUpload.value = null;
    };
    const confirmPicker = async (pages) => {
        const upload = pickerUpload.value;
        closePicker();
        const bestUploadKey = upload?.errorDetails?.bestUploadKey;
        if (!upload || !bestUploadKey)
            return;
        const alone = !upload.sendToPayer;
        upload.error = null;
        upload.preparing = true;
        upload.payerLocked = alone || upload.target !== null;
        try {
            const outcome = await subsetChart(bestUploadKey, pages, { otherLinks: alone ? [] : budgetLinks() });
            if (!pending.value.includes(upload))
                return;
            if (outcome.status === "error")
                failWith(upload, failureMessage(outcome, !alone && payerLinks().length > 0), outcome.errorDetails);
            else {
                const label = upload.label || upload.file?.name || "Document";
                if (outcome.pageRanges)
                    upload.label = `${label} (pages ${outcome.pageRanges})`;
                finish(upload, { ...outcome, link: outcome.downloadUrl });
            }
        }
        catch (error) {
            failWith(upload, tryParseApiErrors(error)[0]);
        }
        finally {
            upload.preparing = false;
            upload.payerLocked = upload.target !== null;
        }
    };
    return {
        rows,
        notice,
        budgetCaption,
        isUploading,
        addFiles,
        uploadInto,
        remove,
        setSendToPayer,
        updateLabel,
        canChoosePages,
        pickerProps,
        openPicker,
        closePicker,
        confirmPicker,
    };
}
